import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CgSpinner } from 'react-icons/cg';
import { toast } from 'react-toastify';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { TbChevronLeft, TbUsers, TbCalendar, TbCash } from 'react-icons/tb';
import { FaMoneyBill } from 'react-icons/fa';
import PageHeader from '../../components/misc/PageHeader';
import { ContributionPlanWithPayments } from '../../../types/bankAccount';
import * as bankAccountService from '../../../services/bankAccountService';
import {
  BaseTable,
  BaseTableBody,
  BaseTableDataRow,
  BaseTableHeader,
  BaseTableHeaderRow,
  BaseTableSpacerRow,
} from '../../components/tables/BaseTable';
import { formatDate } from '../../../utils/stringUtils';
import { formatEuroCents, formatEuroCentsWithSign } from '../../../utils/bankAccountUtil';
import { RootState } from '../../../redux/reducers';
import { ConnectionType } from '../../../redux/slices/connectionsSlice';
import Button from '../../components/buttons/Button';
import ProgressBar from '../../components/misc/ProgressBar';

export default function ContributionPlanPage() {
  const { t, i18n } = useTranslation('', { keyPrefix: 'page.financial.contributionPlans' });
  const { t: tFreq } = useTranslation('', {
    keyPrefix: 'page.financial.enums.ContributionFrequency',
  });
  const navigate = useNavigate();
  const params = useParams();
  const _connections = useSelector((state: RootState) => state.connections);
  const connections = _.keyBy(
    _connections.filter((c) => c.type === ConnectionType.B2C_CONNECTION),
    (c) => +c.id,
  );
  const [contributionPlan, setContributionPlan] = useState<ContributionPlanWithPayments | null>();
  const paymentsByConnectionId = useMemo(() => {
    if (!contributionPlan?.contributionPayments) {
      return {};
    }

    return _.groupBy(contributionPlan.contributionPayments, (p) => p.connectionId);
  }, [contributionPlan]);

  useEffect(() => {
    if (!params.contributionPlanId) {
      navigate('/financial/sales-collect');
      return;
    }

    bankAccountService
      .getContributionPlanWithPayments(+params.contributionPlanId)
      .then(setContributionPlan)
      .catch(() => setContributionPlan(null));
  }, []);

  // Calculate tile data
  const tileData = useMemo(() => {
    if (!contributionPlan) return null;

    const totalAmount = contributionPlan.amount;
    const totalCollected = contributionPlan.contributionPayments.reduce(
      (sum, payment) => sum + payment.transaction.amount,
      0,
    );
    const memberCount = contributionPlan.appliesTo.length;

    // Calculate due today for a single member
    const installmentAmount = totalAmount / contributionPlan.numberOfInstallments;
    const today = new Date();
    const startDate = new Date(contributionPlan.startDate);
    const daysSinceStart = Math.floor(
      (today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24),
    );

    // Calculate how many installments should be paid by today
    const frequencyDays =
      contributionPlan.frequency === 'MONTHLY'
        ? 30
        : contributionPlan.frequency === 'QUARTERLY'
          ? 90
          : contributionPlan.frequency === 'YEARLY'
            ? 365
            : 0;
    const expectedInstallments = Math.max(0, Math.floor(daysSinceStart / frequencyDays));
    const dueToday = Math.min(expectedInstallments * installmentAmount, totalAmount);

    return {
      planPrice: installmentAmount,
      dueToday,
      memberCount,
      amountCollected: totalCollected,
    };
  }, [contributionPlan]);

  if (contributionPlan === null) {
    toast.error(t('error.notFound'));
    navigate('/financial/sales-collect');
    return null;
  }

  if (contributionPlan === undefined) {
    return <CgSpinner size={150} className="mx-auto mt-20 animate-spin text-primary-300" />;
  }

  return (
    <>
      <Link to="/financial/sales-collect" state={{ activeTab: 'contribution' }}>
        <Button className="mb-2 gap-1 p-0">
          <TbChevronLeft size={20} /> {t('back')}
        </Button>
      </Link>
      <PageHeader title={contributionPlan.name} subtitle={contributionPlan.description} />

      {/* Plan Info */}
      {/* <div className="mb-6 space-y-2 text-sm text-gray-600">
        <p>
          {t('period')}: {formatDate(contributionPlan.startDate)}
        </p>
        <p>
          {t('createModal.frequency')}: {tFreq(contributionPlan.frequency)}
        </p>
        <p>
          {t('paymentPlan')}: {formatEuroCents(contributionPlan.amount, i18n.language)} /{' '}
          {contributionPlan.numberOfInstallments}
        </p>
      </div> */}

      {/* Stats Tiles */}
      {tileData && (
        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {/* Plan Price Tile */}
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FaMoneyBill className="text-primary-600 h-8 w-8" />
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-500">{t('tiles.planPrice')}</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatEuroCents(tileData.planPrice, i18n.language)}
                </p>
              </div>
            </div>
          </div>

          {/* Due Today Tile */}
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TbCalendar className="h-8 w-8 text-secondary" />
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-500">{t('tiles.dueToday')}</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatEuroCents(tileData.dueToday, i18n.language)}
                </p>
              </div>
            </div>
          </div>

          {/* Count Tile */}
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TbUsers className="h-8 w-8 text-primary-300" />
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-500">{t('tiles.count')}</p>
                <p className="text-2xl font-bold text-gray-900">{tileData.memberCount}</p>
              </div>
            </div>
          </div>

          {/* Amount Collected Tile */}
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TbCash className="h-8 w-8 text-success" />
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-500">{t('tiles.amountCollected')}</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatEuroCents(tileData.amountCollected, i18n.language)}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Member Table */}
      <div className="relative w-full overflow-x-auto">
        <BaseTable>
          <BaseTableHeader>
            <BaseTableHeaderRow>
              <HeaderCell>{t('table.name')}</HeaderCell>
              <HeaderCell>{t('table.totalDue')}</HeaderCell>
              <HeaderCell>{t('table.dueTillToday')}</HeaderCell>
              <HeaderCell>{t('table.paid')}</HeaderCell>
              <HeaderCell>{t('table.openTillToday')}</HeaderCell>

              <HeaderCell>{t('table.transactions')}</HeaderCell>
            </BaseTableHeaderRow>
          </BaseTableHeader>
          <BaseTableBody>
            <BaseTableSpacerRow />
            {contributionPlan.appliesTo.map((member) => {
              const { FIRST_NAME, LAST_NAME } = connections[member.connectionId].fields;
              const payments = paymentsByConnectionId[member.connectionId] || [];
              const totalPaid = payments.reduce(
                (sum, payment) => sum + payment.transaction.amount,
                0,
              );

              const totalDue = contributionPlan.amount;

              // Calculate due till today for this member
              const installmentAmount = totalDue / contributionPlan.numberOfInstallments;
              const today = new Date();
              const startDate = new Date(contributionPlan.startDate);
              const daysSinceStart = Math.floor(
                (today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24),
              );

              const frequencyDays =
                contributionPlan.frequency === 'MONTHLY'
                  ? 30
                  : contributionPlan.frequency === 'QUARTERLY'
                    ? 90
                    : contributionPlan.frequency === 'YEARLY'
                      ? 365
                      : 0;
              const expectedInstallments = Math.max(0, Math.floor(daysSinceStart / frequencyDays));
              const dueTillToday = Math.min(expectedInstallments * installmentAmount, totalDue);
              const openTillToday = dueTillToday - totalPaid;

              // Calculate status/percentage
              const percentage = totalDue > 0 ? (totalPaid / totalDue) * 100 : 0;

              return (
                <BaseTableDataRow key={member.connectionId}>
                  <DataCell>
                    <div>
                      <p className="font-medium">
                        {FIRST_NAME} {LAST_NAME}
                      </p>
                    </div>
                  </DataCell>
                  <DataCell>{formatEuroCents(totalDue, i18n.language)}</DataCell>
                  <DataCell>{formatEuroCents(dueTillToday, i18n.language)}</DataCell>
                  <DataCell>{formatEuroCents(totalPaid, i18n.language)}</DataCell>
                  <DataCell>
                    {formatEuroCentsWithSign(openTillToday, i18n.language, false)}
                  </DataCell>

                  <DataCell>{payments.length}</DataCell>
                </BaseTableDataRow>
              );
            })}
          </BaseTableBody>
        </BaseTable>
      </div>
    </>
  );
}

interface HeaderCellProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  className?: string;
}

function HeaderCell({ className, ...props }: HeaderCellProps): JSX.Element {
  return <th className={`py-3 pl-1 text-primary-900 ${className || ''}`} {...props} />;
}

interface DataCellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  className?: string;
}

function DataCell({ className, ...props }: DataCellProps): JSX.Element {
  return (
    <td
      className={`whitespace-nowrap py-4 pl-2 text-sm font-medium text-primary-900 ${className || ''}`}
      {...props}
    />
  );
}
